import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Form, Icon, Modal, Progress, Tab, TabPane } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import SimpleFileUpload from '~/components/SimpleFileUpload'
import ENV from '~/environment';
import { ModalTextHeader, ProgressWrapper, StyledMenuItem } from '../../style'
import { formatNumberWithoutFractions, getUsersBrowserData } from '~/utils/helpers'
import notify from '~/components/Notify'
import { CloudStorageService } from '~/api/services/cloudStorage'
import cloneDeep from 'lodash.clonedeep'
import { LanguageType } from '~/api/services/auth'

interface CreateEditFileModalProps {
  openFilesModal: boolean
  optionsData: {
    languages: LanguageType[]
  }
  handleClose: (reloadFiles?: boolean) => void
  id: string
  folderId: string
}

const CreateEditFileModal: React.FC<CreateEditFileModalProps> = (props) => {
  const { t } = useTranslation()
  const {
    openFilesModal,
    optionsData,
    handleClose,
    id,
    folderId
  } = props

  const [cancelToken, setCancelToken] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState<string | number>(0)
  const [isBusy, setIsBusy] = useState(false)
  const [isBusyUploading, setIsBusyUploading] = useState(false)

  const { handleSubmit, control, reset, setValue, formState, getValues } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (id && openFilesModal) {
      fetchEditFileData()
    } else if (openFilesModal) {
      const languages = []
      optionsData?.languages?.map((language) => {
        languages.push({
          id: language.id,
          title: '',
          order: language.order,
          file: ''
        })
      })
      languages?.sort((a, b) => a.order - b.order)
      reset({ visible: 0, languages })
    }
  }, [openFilesModal, id])

  async function fetchEditFileData() {
    setIsBusy(true)
    try {
      const response = await CloudStorageService.getEditDataFile(id)
      const languages = cloneDeep(response.data.languages)
      optionsData?.languages?.map((language) => {
        const indexLanguage = languages.findIndex(languageResponse => languageResponse.id === language.id)
        if (indexLanguage < 0) {
          languages.push({
            id: language.id,
            title: '',
            order: language.order,
            file: '',
            label: language.label
          })
        } else {
          languages[indexLanguage].order = language.order
        }
      })
      languages?.sort((a, b) => a.order - b.order)
      response.data.languages = languages
      reset(response.data)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  }

  function getMenuItem(pane, index) {
    const errors = formState.errors && Object.keys(formState.errors).length > 0 ? Object.keys(formState.errors?.languages) : []
    const paneHasError = errors.find(errorIndex => index === parseInt(errorIndex))

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  function renderCreateEditFilesForm() {
    const langArray = getValues('id') ? getValues('languages') || optionsData?.languages : optionsData?.languages
    return <>
      <FormFields.Checkbox
        control={control}
        name='visible'
        style={{ marginLeft: 13 }}
        defaultValue={0}
        checkboxValue={1}
        label={t('Visible')}
      />
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeIndex}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={langArray.map((language, index) => {
          const langAdded = getValues(`languages[${index}].filename`) && getValues('id')
          return {
            menuItem: getMenuItem(language.label, index),
            pane: <TabPane
              active={index === activeIndex}
              style={{ border: 'none', marginBottom: 0 }}
              key={language.id}
            >
              <FormFields.Input
                control={control}
                label={t('File title')}
                placeholder={t('File title')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter title')
                  },
                  maxLength: {
                    value: 100,
                    message: t('Max char. is 100')
                  }
                }}
                name={`languages[${index}].title`}
              />
              <FormFields.External
                control={control}
                label={t('File')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please upload a file')
                  }
                }}
                keyName={getValues(`languages[${index}]`)}
                onChange={(file: File) => {
                  setValue(`languages[${index}].file`, file)
                }}
                id={getValues('id') || `languages[${index}].file`}
                name={langAdded ? `languages[${index}].filename` : `languages[${index}].file`}
              >
                <SimpleFileUpload
                  activeTabIndex={parseInt(activeIndex.toString())}
                  sizeLimitInMB={300}
                />
              </FormFields.External>
            </TabPane>
          }
        })}
      />
    </>
  }

  function renderProgressBar(text) {
    const uploadPercent = `${formatNumberWithoutFractions(uploadProgress)}%`
    return <ProgressWrapper>
      <Progress percent={uploadProgress} indicating className='custom-progress' />
      <p className='text-below-progress'>{`${text}: ${uploadPercent}`}</p>
    </ProgressWrapper>
  }

  async function onSubmit(data) {
    const formData = new FormData();
    const browserData = getUsersBrowserData()
    formData.append('visible', data.visible)
    formData.append('folderId', folderId)
    data.languages.forEach(language => {
      if (language.file) {
        formData.append(`file-${language.id}`, language.file);
        delete language.file
      }
    });
    Object.keys(browserData).map((key, index) => {
      formData.append(key, Object.values(browserData)[index]);
    })
    formData.append('languages', JSON.stringify(data.languages));
    const url = data.id ? `/cloud-storage/files/${data.id}/update` : '/cloud-storage/files/store'

    const source = axios.CancelToken.source();
    setCancelToken(source);
    setIsBusyUploading(true);
    try {
      await axios.post(`${ENV.API_URL}${url}`, formData, {
        headers: {
          'Authorization': localStorage.getItem('token')
        },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          setUploadProgress((loaded / total) * 100);
        },
        cancelToken: source.token
      });
      setActiveIndex(0);
      handleClose(true)
      if (data.id) {
        notify(t('Files successfully updated'), 'success')
      } else {
        notify(t('Files successfully updated'), 'success')
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        notify(t('Upload canceled'), 'error');
      } else {
        notify(err?.response?.data?.message || t('General error'), 'error');
      }
    }
    setIsBusyUploading(false);
  }

  function handleCloseModal() {
    setActiveIndex(0)
    handleClose()
  }

  const editModal = getValues('id')

  return <Modal
    open={Boolean(openFilesModal)}
    onClose={() => {
      if (cancelToken) {
        cancelToken.cancel(t('Upload canceled by user'))
      }
      handleCloseModal()
    }}
    closeOnDimmerClick={false}
    as={Form}
    onSubmit={handleSubmit(onSubmit)}
  >
    <Modal.Header style={{ display: 'flex', justifyContent: 'space-between' }}>
      <ModalTextHeader>{editModal ? t('Edit file') : t('Upload file')}</ModalTextHeader>
      <Icon onClick={() => {
        if (cancelToken) {
          cancelToken.cancel('Upload canceled by user');
        }
        handleCloseModal()
      }} style={{ cursor: 'pointer' }} name='close' size='large' />
    </Modal.Header>
    <Modal.Content style={{ position: 'relative' }}>
      {isBusyUploading ? renderProgressBar(t('Uploaded')) : renderCreateEditFilesForm()}
    </Modal.Content>
    <Modal.Actions>
      <Button
        disabled={isBusy}
        positive
      >{editModal ? t('Update') : t('Create')}</Button>
      <Button onClick={() => {
        if (cancelToken) {
          cancelToken.cancel('Upload canceled by user');
        }
        handleCloseModal()
      }} negative type='button'>{t('Close')}</Button>
    </Modal.Actions>
  </Modal>
}
export default CreateEditFileModal